<script setup lang="ts">
import ContentHeading from '@fondof-ui/components/content/ContentHeading.vue'
import ContentHeadingRoot from '@fondof-ui/components/content/ContentHeadingRoot.vue'
import ContentRoot from '@fondof-ui/components/content/ContentRoot.vue'
import ContentText from '@fondof-ui/components/content/ContentText.vue'
import type { TeaserCardItemContentProps } from '@storefront-satch/components/molecules/teaser-card/types'

const props = withDefaults(defineProps<TeaserCardItemContentProps>(), {
  headlineSize: 'medium',
})
</script>
<template>
  <ContentRoot
    :class="{
      'pointer-events-none': !isClickable,
      'opacity-0 transition duration-500': hideContent,
      'opacity-1 transition duration-500': !hideContent,
    }"
    :alignment="props.contentAlignment"
  >
    <ContentHeadingRoot
      v-slot="{ headlineElement, sublineElement }"
      :headline-level="props.headlineLevel || null"
      class="w-full"
    >
      <ContentHeading
        :is="headlineElement"
        v-if="props.headline || props.subline"
        :class="[
          'break-words !leading-[1.3]',
          {
            'font-sans-bold text-lg': props.headlineSize === 'large',
            'font-sans-bold text-md': props.headlineSize === 'medium',
            'font-sans-bold text-sm': props.headlineSize === 'small',
            'font-sans-semi-bold text-xs md:text-base':
              props.headlineSize === 'extraSmall',
            'mb-[0.25em]': props.subline || props.text,
            'text-black': props.contentStyle === 'black',
            'text-primary': props.contentStyle === 'brand color',
            'text-white': !props.contentStyle || props.contentStyle === 'white',
          },
        ]"
      >
        {{ props.headline }}
      </ContentHeading>

      <ContentHeading
        :is="sublineElement"
        v-if="props.subline"
        class="text-base"
        :class="{
          'mb-[0.25em]': props.text,
          'text-black': props.contentStyle === 'black',
          'text-primary': props.contentStyle === 'brand color',
          'text-white': !props.contentStyle || props.contentStyle === 'white',
        }"
      >
        {{ props.subline }}
      </ContentHeading>
    </ContentHeadingRoot>

    <ContentText
      v-if="props.text"
      :class="[
        'pr-2.5',
        {
          '!leading-[1.3]': props.isTextCompact,
          '!leading-[1.5]': !props.isTextCompact,
          'text-xs': props.textSize === 'extraSmall',
          'text-sm': props.textSize === 'small',
          'text-base': props.textSize === 'base',
          'text-md': props.textSize === 'medium',
          'text-black': props.contentStyle === 'black',
          'text-primary': props.contentStyle === 'brand color',
          'text-white': !props.contentStyle || props.contentStyle === 'white',
        },
        // paragraphs and links
        'prose-a:underline prose-p:last:mb-0',
        // lists
        'prose-ul:list-outside prose-ol:list-outside prose-ul:list-disc prose-ul:pl-4 prose-ul:m-0 prose-ol:list-decimal prose-ol:pl-4 prose-ol:m-0',
        // list-items
        'prose-li:text-sm prose-li:pl-1.5 prose-li:ml-0 prose-li:indent-0',
      ]"
      style="hyphenate-limit-chars: 6 2 3"
      :content="props.text"
    />

    <slot name="contentFooter" />
  </ContentRoot>
</template>
